import { child, push } from "@firebase/database";
import { createReference } from "../lib/firebase";
import { FirebaseStore } from "../lib/FirebaseStore";

const FIREBASE_KEY = "dashboard";
const LOG_DEBOUNCE_MS = 200;

export interface DashboardLogEntry {
  id: string;
  timestamp: number;
  level: "debug" | "info" | "warn" | "error";
  message: string;
  context: Record<string, any> | null;
}

export interface DashboardData {
  currentInterface: string | null;
  activeDashboardId: string | null;
  currentStepIndex: number;
  logs?: { [logId: string]: DashboardLogEntry } | null;
}

interface DashboardOptions {
  eventPrefix: string;
}

export class DashboardStore extends FirebaseStore<DashboardData> {
  private pendingLogs: DashboardLogEntry[] = [];
  private writeLogsTimeout: NodeJS.Timeout | null = null;

  constructor({ eventPrefix }: DashboardOptions) {
    super(createReference(eventPrefix, FIREBASE_KEY));
  }

  public log(level: DashboardLogEntry["level"], message: string, context?: Record<string, any>) {
    const logId = Date.now().toString(36) + Math.random().toString(36);
    const log: DashboardLogEntry = {
      id: logId,
      timestamp: Date.now(),
      level,
      message,
      context: context ? (sanitizeForFirebase(context) as Record<string, any>) : null,
    };

    this.pendingLogs.push(log);
    this.scheduleWrite();
  }

  private scheduleWrite(): void {
    if (this.writeLogsTimeout) {
      clearTimeout(this.writeLogsTimeout);
    }

    this.writeLogsTimeout = setTimeout(() => {
      this.flushLogs();
    }, LOG_DEBOUNCE_MS);
  }

  public async flushLogs(): Promise<void> {
    if (this.pendingLogs.length === 0) {
      return;
    }

    const logsToWrite: Record<string, DashboardLogEntry> = Object.fromEntries(
      await Promise.all(
        this.pendingLogs.map(async (log) => [
          await push(child(this._ref, "logs")).then((ref) => ref.key),
          log,
        ]),
      ),
    );

    this.pendingLogs = [];

    if (this.writeLogsTimeout) {
      clearTimeout(this.writeLogsTimeout);
      this.writeLogsTimeout = null;
    }

    // Write all logs in a single transaction
    await this.transaction((data) => {
      const logs = data?.logs ?? {};

      return {
        ...(data as any),
        logs: {
          ...logs,
          ...logsToWrite,
        },
      };
    });

    await this.cleanOldLogs();
  }

  async cleanOldLogs(): Promise<void> {
    await this.transaction((data) => {
      const logs = data?.logs ?? {};

      if (Object.keys(logs).length < 500 || !data) {
        return data;
      }

      const orderedLogs = Object.keys(logs).sort((a, b) => logs[b].timestamp - logs[a].timestamp);
      const toDelete = orderedLogs.slice(500);

      return {
        ...data,
        logs: Object.fromEntries(Object.entries(logs).filter(([id]) => !toDelete.includes(id))),
      };
    });
  }
}

// Firebase RTDB rejects writes that contain `undefined`. Drop undefined object keys,
// replace undefined array slots with null, and serialize Errors so logging an Error
// instance doesn't collapse to `{}` due to non-enumerable properties.
function sanitizeForFirebase(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    return value.map((v) => (v === undefined ? null : sanitizeForFirebase(v)));
  }
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      const sanitized = sanitizeForFirebase(v);
      if (sanitized !== undefined) out[k] = sanitized;
    }
    return out;
  }
  if (typeof value === "function" || typeof value === "symbol") return undefined;
  if (typeof value === "bigint") return value.toString();
  return value;
}
